/**
 * "Ask your AI" shortcut for the custom character builder.
 *
 * A user building a character from scratch has to invent a name, write a bio
 * and place five personality sliders. Anyone who already works with ChatGPT or
 * Claude has an assistant that knows how they like to be helped, so we hand
 * them a prompt to paste there, and parse the JSON it answers with back into
 * the form. The prompt asks for a fenced JSON block; the parser tolerates the
 * chatter models wrap it in.
 */
import type {
  PersonalityTraits
} from '../services/api'
import {
  BIO_MAX_LENGTH,
  LEVEL_VALUES,
  NAME_MAX_LENGTH,
  TRAIT_KEYS,
  TRAIT_LEVELS,
} from './customCharacterTraits'

export interface CustomCharacterSuggestion {
  name: string
  bio: string
  traits: PersonalityTraits
}

export function buildCharacterSuggestionPrompt(): string {
  const traitLines = TRAIT_KEYS.map((key) => {
    const scale = TRAIT_LEVELS[key].map((level, i) => `${i + 1} = ${level.label}`).join(', ')
    return `- ${key}: ${scale}`
  }).join('\n')
  return `I'm setting up Catch, an AI executive assistant that handles my calendar, email and travel. I get to design its character. Based on everything you know about me from our work together — how I communicate, what I care about, what kind of help I respond to — propose the assistant character you think would suit me best.

Give it:
1. A first name (a real human first name, under ${NAME_MAX_LENGTH} characters).
2. A short bio, under ${BIO_MAX_LENGTH} characters: who they are and how they work, written in the third person. People I email can read it on the assistant's public profile, so leave out my own name and personal details (the assistant's name is fine).
3. Personality traits, each an integer from 1 to 5 on these scales:
${traitLines}

Reply with ONLY a JSON code block in exactly this shape, so I can paste it straight back:

\`\`\`json
{
  "name": "...",
  "bio": "...",
  "personality_traits": {
${TRAIT_KEYS.map((key) => `    "${key}": 3`).join(',\n')}
  }
}
\`\`\``
}

function extractJsonObject(text: string): string | null {
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(text)
  const candidate = fenced?.[1] ?? text
  const start = candidate.indexOf('{')
  const end = candidate.lastIndexOf('}')
  return start === -1 || end <= start ? null : candidate.slice(start, end + 1)
}

/** Accepts the 1-5 integer the prompt asks for, or a level label if the model named it. */
function traitToValue(key: keyof PersonalityTraits, raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isInteger(raw) && raw >= 1 && raw <= 5) {
    return LEVEL_VALUES[raw - 1]!
  }
  if (typeof raw === 'string') {
    const index = TRAIT_LEVELS[key].findIndex(
      (level) => level.label.toLowerCase() === raw.trim().toLowerCase(),
    )
    if (index !== -1) return LEVEL_VALUES[index]!
    const parsed = Number(raw)
    if (Number.isInteger(parsed) && parsed >= 1 && parsed <= 5) return LEVEL_VALUES[parsed - 1]!
  }
  return null
}

/**
 * Parse a pasted model reply into form values. Returns null when no usable
 * JSON is found, or when the name or bio are missing. A trait the model left
 * out or garbled falls back to the middle level rather than failing the whole
 * paste — the sliders are right there to fix it.
 */
export function parseCharacterSuggestion(text: string): CustomCharacterSuggestion | null {
  const json = extractJsonObject(text)
  if (!json) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(json)
  } catch {
    return null
  }
  if (typeof parsed !== 'object' || parsed === null) return null
  const record = parsed as Record<string, unknown>
  const name = typeof record['name'] === 'string' ? record['name'].trim() : ''
  const bio = typeof record['bio'] === 'string' ? record['bio'].trim() : ''
  if (!name || !bio) return null

  const rawTraits = record['personality_traits'] ?? record['traits']
  const traitRecord =
    typeof rawTraits === 'object' && rawTraits !== null
      ? (rawTraits as Record<string, unknown>)
      : {}
  const traits = Object.fromEntries(
    TRAIT_KEYS.map((key) => [key, traitToValue(key, traitRecord[key]) ?? 50]),
  ) as unknown as PersonalityTraits

  return {
    name: name.slice(0, NAME_MAX_LENGTH),
    bio: bio.slice(0, BIO_MAX_LENGTH),
    traits,
  }
}
